import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, RefreshCw, Trash2, Square } from 'lucide-react';
import { api } from '../services/api';
const STATUS_CONFIG = {
  running: { color: '#8B5CF6', bg: 'rgba(139,92,246,0.1)', label: 'Evolving' },
  completed: { color: '#34D399', bg: 'rgba(52,211,153,0.1)', label: 'Completed' },
  failed: { color: '#F87171', bg: 'rgba(248,113,113,0.1)', label: 'Failed' },
  promoted: { color: '#60A5FA', bg: 'rgba(96,165,250,0.1)', label: 'Promoted' },
};

export default function EvolutionDetailView({ record, onBack }) {
  const iframeRef = useRef(null);
  const [reportHtml, setReportHtml] = useState(null);
  const [loadingReport, setLoadingReport] = useState(true);
  const [iframeHeight, setIframeHeight] = useState(400);
  const [currentStatus, setCurrentStatus] = useState(record?.status);

  const statusCfg = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.failed;

  // Fetch HTML report
  useEffect(() => {
    if (!record?.skillSlug) return;
    setLoadingReport(true);

    api.authFetch(`/api/runner/evolution/report/${record.skillSlug}`)
      .then(r => {
        if (!r.ok) throw new Error('No report');
        return r.text();
      })
      .then(html => {
        setReportHtml(html);
        setLoadingReport(false);
      })
      .catch(() => {
        setReportHtml(null);
        setLoadingReport(false);
      });
  }, [record?.skillSlug]);

  // Auto-resize iframe
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === 'resize' && typeof e.data.height === 'number') {
        setIframeHeight(Math.min(Math.max(e.data.height, 200), window.innerHeight * 0.7));
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // Poll for running evolutions
  useEffect(() => {
    if (currentStatus !== 'running') return;
    const timer = setInterval(() => {
      api.authFetch(`/api/runner/evolution/status`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (!data) return;
          const updated = (data.history || []).find(
            r => r.skillSlug === record.skillSlug && r.timestamp === record.timestamp
          );
          if (updated && updated.status !== 'running') {
            setCurrentStatus(updated.status);
            // Re-fetch report
            api.authFetch(`/api/runner/evolution/report/${record.skillSlug}`)
              .then(r => r.ok ? r.text() : null)
              .then(html => { if (html) setReportHtml(html); })
              .catch(() => {});
          }
        })
        .catch(() => {});
    }, 10000);
    return () => clearInterval(timer);
  }, [currentStatus, record?.skillSlug, record?.timestamp]);

  const handleTerminate = useCallback(() => {
    if (!record?.skillSlug) return;
    api.authFetch(`/api/runner/evolution/terminate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ skillSlug: record.skillSlug }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.terminated) setCurrentStatus('failed');
      })
      .catch(() => {});
  }, [record?.skillSlug]);

  const handleDelete = useCallback(() => {
    if (!record?.skillSlug || !record?.timestamp) return;
    api.authFetch(`/api/runner/evolution/record`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ skillSlug: record.skillSlug, timestamp: record.timestamp }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.deleted) onBack?.();
      })
      .catch(() => {});
  }, [record?.skillSlug, record?.timestamp, onBack]);

  if (!record) return null;

  const date = new Date(record.timestamp);
  const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  // Wrap report HTML with postMessage resize script
  const wrappedHtml = reportHtml ? reportHtml.replace('</body>', `
    <script>
      new ResizeObserver(() => {
        parent.postMessage({ type: 'resize', height: document.body.scrollHeight + 40 }, '*');
      }).observe(document.body);
      parent.postMessage({ type: 'resize', height: document.body.scrollHeight + 40 }, '*');
    </script>
    </body>`) : null;

  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      className="w-full h-full flex flex-col"
      style={{ background: '#F2F2F7' }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 pt-3 pb-2" style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}>
        <button onClick={onBack} className="p-1 -ml-1 active:opacity-50">
          <ArrowLeft size={22} color="#000" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate" style={{ fontSize: 17 }}>
            {record.skillSlug}
          </div>
          <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.4)' }}>
            {dateStr} · {timeStr}
          </div>
        </div>
        <span
          className="px-3 py-1 rounded-full"
          style={{ fontSize: 12, fontWeight: 600, background: statusCfg.bg, color: statusCfg.color }}
        >
          {currentStatus === 'running' && (
            <motion.span
              className="inline-block w-1.5 h-1.5 rounded-full mr-1.5"
              style={{ background: statusCfg.color, verticalAlign: 'middle' }}
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          )}
          {statusCfg.label}
        </span>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-4 pb-6">
        {/* Metrics cards */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <MetricCard label="Baseline" value={`${record.oldPassRate || 0}%`} />
          <MetricCard label="After" value={`${record.newPassRate || 0}%`} />
          <MetricCard
            label="Delta"
            value={`${(record.newPassRate || 0) - (record.oldPassRate || 0) > 0 ? '+' : ''}${(record.newPassRate || 0) - (record.oldPassRate || 0)}%`}
            color={(record.newPassRate || 0) - (record.oldPassRate || 0) > 0 ? '#34D399' : (record.newPassRate || 0) - (record.oldPassRate || 0) < 0 ? '#F87171' : '#9CA3AF'}
          />
        </div>

        <div className="grid grid-cols-2 gap-2 mb-4">
          <MetricCard label="Iterations" value={record.iterations || 0} />
          <MetricCard label="Sessions" value={record.sessionCount || 0} />
        </div>

        {/* Skill update status */}
        <div
          className="mb-4 px-4 py-3 rounded-2xl"
          style={{ background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
        >
          <div className="flex items-center gap-2">
            {currentStatus === 'completed' ? (
              <>
                <span style={{ color: '#34D399', fontSize: 16 }}>✓</span>
                <span style={{ fontSize: 14, fontWeight: 500 }}>Skill Hot-Updated</span>
              </>
            ) : (
              <>
                <span style={{ color: '#F87171', fontSize: 16 }}>✗</span>
                <span style={{ fontSize: 14, fontWeight: 500 }}>Skill Not Updated</span>
              </>
            )}
          </div>
          {record.draftPath && (
            <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.3)', marginTop: 4, fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {record.draftPath}
            </div>
          )}
        </div>

        {/* HTML Report */}
        <div
          className="rounded-2xl overflow-hidden mb-4"
          style={{ background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
        >
          <div className="px-4 py-2.5 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Evolution Report</span>
            {currentStatus === 'running' && (
              <RefreshCw size={14} color="rgba(0,0,0,0.3)" className="animate-spin" />
            )}
          </div>

          {loadingReport ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-5 h-5 border-2 border-purple-200 border-t-purple-500 rounded-full animate-spin" />
            </div>
          ) : wrappedHtml ? (
            <iframe
              ref={iframeRef}
              srcDoc={wrappedHtml}
              // Issue #1411 follow-up: ``allow-same-origin`` lets embedded
              // ``<img src="/api/media/...">`` inherit the parent cookie
              // for the owner-auth proxy. Without it the iframe runs at a
              // null opaque origin and every media fetch 401s.
              sandbox="allow-scripts allow-same-origin"
              style={{
                width: '100%',
                height: iframeHeight,
                border: 'none',
                display: 'block',
              }}
              title="Evolution Report"
            />
          ) : currentStatus === 'running' ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <motion.div
                className="w-8 h-8 rounded-full"
                style={{ background: 'rgba(139,92,246,0.15)' }}
                animate={{ scale: [1, 1.3, 1], opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.4)' }}>
                Evolution in progress...
              </span>
              <span style={{ fontSize: 11, color: 'rgba(0,0,0,0.25)' }}>
                Report will appear when benchmarks complete
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.3)' }}>
                No report available
              </span>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          {currentStatus === 'running' && (
            <button
              onClick={handleTerminate}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl active:scale-[0.98] transition-transform"
              style={{ background: 'rgba(248,113,113,0.1)', color: '#DC2626', fontSize: 14, fontWeight: 500 }}
            >
              <Square size={14} />
              Terminate
            </button>
          )}
          <button
            onClick={handleDelete}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl active:scale-[0.98] transition-transform"
            style={{ background: 'rgba(0,0,0,0.04)', color: 'rgba(0,0,0,0.5)', fontSize: 14, fontWeight: 500 }}
          >
            <Trash2 size={14} />
            Delete
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function MetricCard({ label, value, color }) {
  return (
    <div
      className="rounded-2xl px-3 py-3 text-center"
      style={{ background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
    >
      <div style={{ fontSize: 24, fontWeight: 700, color: color || '#000' }}>{value}</div>
      <div style={{ fontSize: 10, color: 'rgba(0,0,0,0.35)', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </div>
    </div>
  );
}
